import { useEffect, useState, useContext, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { getLanguages } from "../../api/languageApi";
import { AuthContext } from "../../Context/AuthContext";

export default function CountryForm({ visible, onHide, onSubmit, initialData, existingCountries = [] }) {
  const [form, setForm] = useState({ code: "", name: "", languageIds: [] });
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  const loadLanguages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getLanguages(token);
      setLanguages(res.data.data || []);
    } catch (err) {
      console.error("Failed to load languages", err);
    }
  }, [token]);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    if (!visible) return;
    if (initialData) {
      setForm({
        code: initialData.code,
        name: initialData.name,
        languageIds: initialData.Languages?.map(l => l.id) || [],
      });
    } else {
      setForm({ code: "", name: "", languageIds: [] });
    }
  }, [visible, initialData]);

  const toSentenceCase = str => {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/(^\s*\w|[\.\!\?]\s*\w)/g, c => c.toUpperCase());
  };

  const submitForm = async () => {
    const code = form.code.trim().toLowerCase();
    const name = toSentenceCase(form.name.trim());

    if (!code || !name) {
      alert("Country code and name are required");
      return;
    }

    const duplicateCode = existingCountries.some(c =>
      c.code.toLowerCase() === code && c.id !== initialData?.id
    );

    const duplicateName = existingCountries.some(c =>
      c.name.toLowerCase() === name.toLowerCase() && c.id !== initialData?.id
    );

    if (duplicateCode) {
      alert("Duplicate country code detected");
      return;
    }

    if (duplicateName) {
      alert("Duplicate country name detected");
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        code,
        name,
        languageIds: form.languageIds || [],
      });

      if (!initialData) setForm({ code: "", name: "", languageIds: [] });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
  const handleKeyDown = (e) => {
    const tag = e.target.tagName.toLowerCase();
    if (e.key === "Enter" && (tag === "input" || tag === "textarea")) {
      e.preventDefault();
      submitForm();
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [form, initialData, existingCountries]);


  return (
    <Dialog
      header={initialData ? "Edit Country" : "Add Country"}
      visible={visible}
      onHide={onHide}
      modal
      className="w-6"
    >
      <div className="flex flex-column gap-3 p-3">
        <div>
          <label>Country Name</label>
          <InputText
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full"
          />
        </div>

        <div>
          <label>Country Code</label>
          <InputText
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            className="w-full"
          />
        </div>

        <div>
          <label>Languages</label>
          <MultiSelect
            value={form.languageIds}
            options={languages}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, languageIds: e.value })}
            placeholder="Select Languages"
            className="w-full"
          />
        </div>

        <Button
          label={isLoading ? "Submitting..." : "Submit"}
          icon={isLoading ? "pi pi-spin pi-spinner" : ""}
          onClick={submitForm}
          disabled={isLoading}
          className=" p-button-block custom-splitbutton"
          style={{ backgroundColor: "#FC5F1CFF", color: "#fff" }}
        />
      </div>
    </Dialog>
  );
}
